import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { useCallback, useEffect, useMemo, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { SEATINGS_ENABLED } from "../seatings/seatings-enabled";
import { useHiddenNotificationIds } from "./notification-deletion";
import { notificationSchema, type Notification } from "./schemas";
import { markNotificationRead } from "./use-notification";

/**
 * One listener serves both the badge and the feed, so the cap has to be large
 * enough that "unread" stays honest for anyone who opens the app at least
 * occasionally. Beyond it the badge saturates rather than paying for a second
 * count query.
 */
export const FEED_LIMIT = 50;

export type NotificationsState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: Notification[] };

interface NotificationsResult {
  state: NotificationsState;
  unreadCount: number;
  markRead: (notification: Notification) => Promise<void>;
  markAllRead: () => Promise<void>;
}

/**
 * The signed-in user's inbox, kept live: the badge is the whole point of the
 * bell, and a stale one teaches people to ignore it. Documents are written only
 * by backend triggers; the client may flip `readAt` to the request time or
 * delete a document (see notification-deletion.ts). Items waiting out their
 * undo window are left out of both the feed and the unread count.
 */
export function useNotifications(uid: string): NotificationsResult {
  const [loaded, setState] = useState<NotificationsState>({
    status: "loading",
  });
  const hidden = useHiddenNotificationIds();
  const state = useMemo<NotificationsState>(
    () =>
      loaded.status === "success" && hidden.size > 0
        ? {
            status: "success",
            data: loaded.data.filter(
              (notification) => !hidden.has(notification.id),
            ),
          }
        : loaded,
    [loaded, hidden],
  );

  useEffect(
    () =>
      onSnapshot(
        query(
          collection(db, "users", uid, "notifications"),
          orderBy("createdAt", "desc"),
          limit(FEED_LIMIT),
        ),
        (snapshot) => {
          const notifications: Notification[] = [];
          snapshot.forEach((document) => {
            const parsed = notificationSchema.safeParse(document.data());
            // A dinner-table notification opens an Overview that shows no
            // dinner table while seatings are off, so it stays out of the
            // feed and the badge. The document is untouched.
            if (
              parsed.success &&
              (SEATINGS_ENABLED || parsed.data.type !== "SEATING_TABLE")
            )
              notifications.push({ ...parsed.data, id: document.id });
          });
          setState({ status: "success", data: notifications });
        },
        (error) =>
          setState({
            status: "error",
            error:
              error instanceof Error
                ? error.message
                : "Failed to load notifications",
          }),
      ),
    [uid],
  );

  const unreadCount =
    state.status === "success"
      ? state.data.filter((notification) => notification.readAt === null).length
      : 0;

  const markRead = useCallback(
    (notification: Notification) => markNotificationRead(uid, notification),
    [uid],
  );

  const markAllRead = useCallback(async () => {
    if (state.status !== "success") return;
    const unread = state.data.filter(
      (notification) => notification.readAt === null,
    );
    if (unread.length === 0) return;
    const batch = writeBatch(db);
    for (const notification of unread) {
      batch.update(doc(db, "users", uid, "notifications", notification.id), {
        readAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }, [state, uid]);

  return { state, unreadCount, markRead, markAllRead };
}
